import { useGetCurrentEmployee } from "#resources/hooks/integrations/auth/query";
import { useGetCurrentOrganization } from "#resources/hooks/integrations/org/query";
import { useSimplifiedPlaces } from "#resources/hooks/integrations/place/query";
import { useGetZendeskMessagingJwt } from "#resources/hooks/integrations/zendesk/query";
import { contextLevelFromUrl } from "#resources/helpers/url/url";
import type { PlaceSize } from "#resources/api/enterprise-generated";
import { useStores } from "#stores/index";
import {
	closeZendeskChat,
	logoutZendesk,
	openZendeskChat,
	setZendeskAuthToken,
	setZendeskOpenIntent,
	ZENDESK_FIELD_IDS,
	ZendeskConversationField,
	ZendeskPlaceSizeValue,
	ZendeskPlaceTypeValue,
} from "#structure/providers/zendesk";

function mapPlaceSize(size: PlaceSize | null): ZendeskPlaceSizeValue {
	switch (size) {
		case "XS":
			return "pp_tamanho_casa";
		case "S":
			return "p_tamanho_casa";
		case "M":
			return "m_tamanho_casa";
		case "L":
			return "g_tamanho_casa";
		case "XL":
			return "mega_tamanho_casa";
		default:
			return "não_identificado_tamanho_casa";
	}
}

function mapPlaceType(isHouse: boolean | null): ZendeskPlaceTypeValue {
	if (isHouse === true) return "sim_casa_evento";
	if (isHouse === false) return "não_casa_evento";
	return "não_se_aplica_evento";
}

export function useZendeskSupport() {
	const { data: currentEmployee } = useGetCurrentEmployee({});
	const { data: currentOrganization } = useGetCurrentOrganization({});
	const { data: simplifiedPlaces } = useSimplifiedPlaces({});
	const { placeStore, eventStore } = useStores();
	const contextLevel = contextLevelFromUrl();

	const { refetch } = useGetZendeskMessagingJwt(undefined, {
		enabled: false,
		retry: false,
	});

	const buildFields = (): ZendeskConversationField[] => {
		const currentSimplifiedPlace = simplifiedPlaces?.find(
			p => p.id === placeStore.place?.id,
		);

		const fields: ZendeskConversationField[] = [
			{ id: ZENDESK_FIELD_IDS.userName, value: currentEmployee?.name ?? "" },
			{ id: ZENDESK_FIELD_IDS.requesterName, value: currentEmployee?.name ?? "" },
			{ id: ZENDESK_FIELD_IDS.requesterEmail, value: currentEmployee?.email ?? "" },
			{
				id: ZENDESK_FIELD_IDS.dashPermissions,
				value: (currentEmployee?.permissions ?? [])
					.filter(p => p.type === "Enterprise")
					.map(p => p.name)
					.join("\n"),
			},
			{
				id: ZENDESK_FIELD_IDS.organizationId,
				value: currentEmployee?.organization.id ?? "",
			},
			{
				id: ZENDESK_FIELD_IDS.organizationName,
				value: currentEmployee?.organization.name ?? "",
			},
			{
				id: ZENDESK_FIELD_IDS.orgLogin,
				value: currentEmployee?.organization.username ?? "",
			},
			{ id: ZENDESK_FIELD_IDS.source, value: "Dashboard" },
			{
				id: ZENDESK_FIELD_IDS.isKeyAccount,
				value: currentOrganization?.isKeyAccount ? "sim_cliente" : "não_cliente",
			},
		];

		if (contextLevel === "place" || contextLevel === "event") {
			fields.push(
				{ id: ZENDESK_FIELD_IDS.placeId, value: placeStore.place?.id ?? "" },
				{ id: ZENDESK_FIELD_IDS.placeName, value: placeStore.place?.name ?? "" },
				{
					id: ZENDESK_FIELD_IDS.placeSize,
					value: mapPlaceSize(currentSimplifiedPlace?.size ?? null),
				},
				{
					id: ZENDESK_FIELD_IDS.placeType,
					value: mapPlaceType(currentSimplifiedPlace?.isHouse ?? null),
				},
			);
		}

		if (contextLevel === "event") {
			fields.push(
				{ id: ZENDESK_FIELD_IDS.eventId, value: eventStore.event?.id ?? "" },
				{ id: ZENDESK_FIELD_IDS.eventName, value: eventStore.event?.name ?? "" },
			);
		}

		return fields;
	};

	console.log(buildFields());

	const openSupport = async () => {
		setZendeskOpenIntent(true);
		let token: string | undefined;
		try {
			const result = await refetch();
			token = result.data;
		} catch {}
		if (token) setZendeskAuthToken(token);
		openZendeskChat(buildFields());
	};

	const closeSupport = () => {
		closeZendeskChat();
		logoutZendesk();
	};

	return { openSupport, closeSupport };
}
